import {
    Broadcast,
    Channel,
    CoverAsset,
    DetailEpisode,
    Episode,
    Movie,
    MovieDetails,
    NowAndNext,
    Person,
    PlaceholderAsset,
    ProgramDetails,
    Recording,
    RecordingDetails,
    Series,
    TVSeriesDetailEpisode,
    ContinueWatchingItems,
    EpisodeCard,
    MovieCard,
} from 'types/Asset';
import { ChannelCover } from 'types/Cover';
import {
    CatchupPlayerAssetInfo,
    LivePlayerAssetInfo,
    PlayingAsset,
    PlayingAssetType,
    RecordingPlayerAssetInfo,
    TrailerPlayerAssetInfo,
    VodPlayerAssetInfo,
} from 'types/Player';
import { AssetTypes } from 'types/Common';
import { AiringTime, Rentable, Subscribable, TVEpisodeInfo } from 'types/CommonTypes';

export function isPlaceholder(cover: CoverAsset): cover is PlaceholderAsset {
    return (cover as PlaceholderAsset).type === AssetTypes.placeholder;
}

export function isChannel(cover: CoverAsset): cover is Channel {
    return (cover as Channel).type === AssetTypes.channel;
}

export function isTVChannel(cover: any): cover is ChannelCover {
    return (cover as ChannelCover)?.assetType === AssetTypes.channel;
}

export function isBroadcast(cover: CoverAsset): cover is Broadcast {
    return (cover as Broadcast).type === AssetTypes.program;
}

export function isRecording(cover: CoverAsset): cover is Recording {
    return (cover as Recording).type === AssetTypes.recording;
}

export function isTVEpisodeBroadcast(asset: any): asset is TVEpisodeInfo {
    const tvEpisodeInfo = asset as TVEpisodeInfo;
    return tvEpisodeInfo?.seriesId != null || tvEpisodeInfo?.episodeId != null;
}

export function isEpisodeRecording(recording: Recording) {
    return recording.seriesId != null;
}

export function isNowAndNext(cover: CoverAsset): cover is NowAndNext {
    return (cover as NowAndNext).type === AssetTypes.now_next;
}

export function isContinueWatching(cover: CoverAsset): cover is ContinueWatchingItems {
    if ((cover as CoverAsset).type !== AssetTypes.continue_watching) {
        return false;
    }

    const asset = cover as ContinueWatchingItems;
    return Array.isArray(asset.production_countries);
}

export function isMovie(cover: CoverAsset): cover is Movie {
    return (cover as Movie).type === AssetTypes.movie || (cover as Movie).type === AssetTypes.vod_asset;
}

export function isMovieCard(cover: CoverAsset): cover is MovieCard {
    return (cover as MovieCard).type === AssetTypes.movie || (cover as MovieCard).type === AssetTypes.vod_asset;
}

export function isProgramMovie(cover: CoverAsset): cover is Movie {
    const asset = cover as Movie;
    return (asset.category === AssetTypes.movies || (asset as any).kind === 'Movie') && asset.listings?.length > 0;
}

export function isTVOD(cover: CoverAsset): cover is Movie {
    return cover.vod_terms?.some((term) => term.type === AssetTypes.tvod);
}

export function isSVOD(cover: CoverAsset): cover is Movie {
    return cover.vod_terms?.some((term) => term.type === 'SVOD');
}

export function isSeries(cover: CoverAsset): cover is Series {
    return (
        (cover as Series).type === AssetTypes.series ||
        (cover as Series).type === AssetTypes.program_series ||
        (cover as DetailEpisode).type === AssetTypes.vod_series
    );
}

export function isEpisode(cover: CoverAsset): cover is Episode {
    return (cover as Episode).type === AssetTypes.episode || (cover as Episode).type === AssetTypes.tv_series_episode;
}

export function isVODSeriesEpisode(cover: CoverAsset): cover is DetailEpisode {
    return (cover as DetailEpisode).type === AssetTypes.vod_episode && !!(cover as DetailEpisode).vod_series_id;
}

export function isVODEpisodeCard(cover: CoverAsset): cover is EpisodeCard {
    return (cover as EpisodeCard).type === AssetTypes.vod_episode && !!(cover as EpisodeCard).vod_series_id;
}

export function isTvSeriesEpisode(cover: CoverAsset): cover is TVSeriesDetailEpisode {
    if ((cover as any).vod_series_id) {
        return false;
    }

    if (cover.type) {
        return (cover as TVSeriesDetailEpisode).type === AssetTypes.tv_series_episode;
    }
    if (cover.kind) {
        return (({ kind }) => kind === AssetTypes.episode || kind === AssetTypes.show)(cover as TVSeriesDetailEpisode);
    }
    return false;
}

export function isPerson(cover: CoverAsset): cover is Person {
    return (cover as Person).type === AssetTypes.person;
}

export function isMovieDetails(asset): asset is MovieDetails {
    return (asset as MovieDetails).type === AssetTypes.movie;
}

export function isProgramDetails(asset: any): asset is ProgramDetails {
    return (asset as ProgramDetails).type === AssetTypes.program;
}

export function isRecordingDetails(asset: any): asset is RecordingDetails {
    return (asset as RecordingDetails).type === AssetTypes.recording;
}

export function isRentable(asset): asset is Rentable {
    return (asset as Rentable).vodAssetState != null;
}

export function isSubscribable(asset): asset is Subscribable {
    return (asset as Subscribable).subscribeOffer != null;
}

export function isWithAiringTime(asset): asset is AiringTime {
    const airing = asset as AiringTime;
    return 'startTime' in airing && 'endTime' in airing;
}

export function isLivePlayerAsset(asset: PlayingAsset): asset is LivePlayerAssetInfo {
    return asset && (asset as LivePlayerAssetInfo).use_case === PlayingAssetType.LIVE;
}

export function isCatchupPlayerAsset(asset: PlayingAsset): asset is CatchupPlayerAssetInfo {
    return asset && (asset as CatchupPlayerAssetInfo).use_case === PlayingAssetType.REPLAY;
}

export function isRecordingPlayerAsset(asset: PlayingAsset): asset is RecordingPlayerAssetInfo {
    return asset && (asset as RecordingPlayerAssetInfo).type === PlayingAssetType.RECORDING;
}

export function isTrailerPlayerAsset(asset: PlayingAsset): asset is TrailerPlayerAssetInfo {
    return asset && (asset as TrailerPlayerAssetInfo).type === PlayingAssetType.TRAILER;
}

export function isVodPlayerAsset(asset: PlayingAsset): asset is VodPlayerAssetInfo {
    return asset && (asset as VodPlayerAssetInfo).type === PlayingAssetType.VOD;
}
